import { useState, useEffect, useRef } from 'react'
import styles from './Contact.module.css'

// ── ICONS ──────────────────────────────────────────────────────────────────────
const SendIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13"/>
    <polygon points="22 2 15 22 11 13 2 9 22 2"/>
  </svg>
)

const CheckIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
)

const MailIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
    <polyline points="22,6 12,13 2,6"/>
  </svg>
)

const BotIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="10" rx="2"/>
    <circle cx="12" cy="5" r="2"/>
    <path d="M12 7v4"/>
    <circle cx="8" cy="16" r="1" fill="currentColor" stroke="none"/>
    <circle cx="16" cy="16" r="1" fill="currentColor" stroke="none"/>
  </svg>
)

const ZapIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
  </svg>
)

const LinkIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
  </svg>
)

const AlertIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <line x1="12" y1="8" x2="12" y2="12"/>
    <line x1="12" y1="16" x2="12.01" y2="16"/>
  </svg>
)

// ── FLOATING PARTICLE ─────────────────────────────────────────────────────────
function Particles() {
  return (
    <div className={styles.particles} aria-hidden>
      {Array.from({ length: 14 }).map((_, i) => (
        <div
          key={i}
          className={styles.particle}
          style={{
            left: `${(i * 7.3 + 5) % 100}%`,
            animationDelay: `${i * 0.4}s`,
            animationDuration: `${6 + (i % 5)}s`,
            width: i % 3 === 0 ? '3px' : '2px',
            height: i % 3 === 0 ? '3px' : '2px',
          }}
        />
      ))}
    </div>
  )
}

// ── CONTACT INFO ITEM ─────────────────────────────────────────────────────────
function InfoItem({ icon, label, value, delay }) {
  return (
    <div className={styles.infoItem} style={{ animationDelay: delay }}>
      <div className={styles.infoIcon}>{icon}</div>
      <div>
        <div className={styles.infoLabel}>{label}</div>
        <div className={styles.infoValue}>{value}</div>
      </div>
    </div>
  )
}

// ── MAIN ──────────────────────────────────────────────────────────────────────
export default function Contact() {
  const [form, setForm]       = useState({ name: '', email: '', message: '' })
  const [status, setStatus]   = useState('idle') // idle | sending | success | error
  const [visible, setVisible] = useState(false)
  const [focused, setFocused] = useState(null)
  const formRef = useRef(null)

  useEffect(() => { setTimeout(() => setVisible(true), 60) }, [])

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }))
  const isValid = form.name.trim() && form.email.trim() && form.message.trim()

  const handleSubmit = async () => {
    if (!isValid || status === 'sending') return
    setStatus('sending')

    const data = new FormData()
    data.append('access_key', '7c5f91b1-28c3-41a5-98ff-12d6047389b2')
    data.append('subject', 'New Message from Saviya-X Contact Form')
    data.append('name', form.name)
    data.append('email', form.email)
    data.append('message', form.message)

    try {
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: data,
      })
      const json = await res.json()
      if (json.success) {
        setStatus('success')
        setForm({ name: '', email: '', message: '' })
      } else {
        setStatus('error')
        setTimeout(() => setStatus('idle'), 4000)
      }
    } catch {
      setStatus('error')
      setTimeout(() => setStatus('idle'), 4000)
    }
  }

  return (
    <div className={`${styles.page} ${visible ? styles.visible : ''}`}>

      {/* ── HERO HEADER ── */}
      <div className={styles.hero}>
        <Particles />
        <div className={styles.heroContent}>
          <div className={styles.breadcrumb}>
            <span>Saviya-X</span>
            <span className={styles.sep}>/</span>
            <span className={styles.current}>Contact</span>
          </div>
          <h1 className={styles.heroTitle}>Let's talk</h1>
          <p className={styles.heroSub}>
            Questions about the network, API access, or custom deployments?
            We respond within 24 hours.
          </p>
        </div>
        <div className={styles.heroOrb} />
      </div>

      {/* ── LAYOUT ── */}
      <div className={styles.layout}>

        {/* ── FORM CARD ── */}
        <div className={styles.formCard} ref={formRef}>
          <div className={styles.shimmerBar} />

          {status === 'success' ? (
            <div className={styles.successState}>
              <div className={styles.successOrb} />
              <div className={styles.successIcon}><CheckIcon /></div>
              <h3 className={styles.successTitle}>Message sent!</h3>
              <p className={styles.successDesc}>
                Thanks for reaching out. We'll get back to you at <strong>{form.email || 'your email'}</strong> within 24 hours.
              </p>
              <button className={styles.resetBtn} onClick={() => setStatus('idle')}>
                Send another message
              </button>
            </div>
          ) : (
            <>
              <div className={styles.formHeader}>
                <div className={styles.formIconWrap}><MailIcon /></div>
                <div>
                  <h3 className={styles.formTitle}>Send a message</h3>
                  <p className={styles.formDesc}>Direct — no tickets, no bots</p>
                </div>
              </div>

              <div className={styles.fields}>

                {/* NAME */}
                <div className={`${styles.field} ${focused === 'name' ? styles.focused : ''}`}>
                  <label className={styles.label}>
                    Your name
                    <span className={styles.required}>*</span>
                  </label>
                  <input
                    className={styles.input}
                    placeholder="e.g. Savithu"
                    value={form.name}
                    onChange={set('name')}
                    onFocus={() => setFocused('name')}
                    onBlur={() => setFocused(null)}
                    autoComplete="name"
                  />
                </div>

                {/* EMAIL */}
                <div className={`${styles.field} ${focused === 'email' ? styles.focused : ''}`}>
                  <label className={styles.label}>
                    Email address
                    <span className={styles.required}>*</span>
                  </label>
                  <input
                    className={styles.input}
                    type="email"
                    placeholder="you@example.com"
                    value={form.email}
                    onChange={set('email')}
                    onFocus={() => setFocused('email')}
                    onBlur={() => setFocused(null)}
                    autoComplete="email"
                  />
                </div>

                {/* MESSAGE */}
                <div className={`${styles.field} ${focused === 'message' ? styles.focused : ''}`}>
                  <label className={styles.label}>
                    Message
                    <span className={styles.required}>*</span>
                  </label>
                  <textarea
                    className={`${styles.input} ${styles.textarea}`}
                    placeholder="Describe what you need — API access, integration help, custom deployment…"
                    rows={5}
                    value={form.message}
                    onChange={set('message')}
                    onFocus={() => setFocused('message')}
                    onBlur={() => setFocused(null)}
                  />
                </div>

                {/* ERROR */}
                {status === 'error' && (
                  <div className={styles.errorBox}>
                    <AlertIcon /> Failed to send. Please try again.
                  </div>
                )}

                {/* SUBMIT */}
                <button
                  className={`${styles.submitBtn} ${status === 'sending' ? styles.sending : ''} ${!isValid ? styles.disabled : ''}`}
                  onClick={handleSubmit}
                  disabled={!isValid || status === 'sending'}
                >
                  <span className={styles.submitInner}>
                    {status === 'sending' ? (
                      <><span className={styles.spinner} /> Sending…</>
                    ) : (
                      <><SendIcon /> Send Message</>
                    )}
                  </span>
                  <span className={styles.submitGlow} />
                </button>

                <p className={styles.formNote}>
                  Powered by Web3Forms · Your data is never sold or shared.
                </p>
              </div>
            </>
          )}
        </div>

        {/* ── SIDE PANEL ── */}
        <div className={styles.side}>

          {/* QUICK INFO */}
          <div className={styles.sideCard}>
            <h4 className={styles.sideTitle}>Get in touch</h4>
            <div className={styles.infoList}>
              <InfoItem
                icon={<ZapIcon />}
                label="Response time"
                value="Within 24 hours"
                delay="0.1s"
              />
              <InfoItem
                icon={<BotIcon />}
                label="Bot integrations"
                value="API docs available"
                delay="0.2s"
              />
              <InfoItem
                icon={<LinkIcon />}
                label="Deploy endpoint"
                value="/dep/bybot?num="
                delay="0.3s"
              />
            </div>
          </div>

          {/* FAQ */}
          <div className={styles.sideCard}>
            <h4 className={styles.sideTitle}>Common topics</h4>
            <div className={styles.faqList}>
              {[
                { q: 'How do I deploy a session?', a: 'Enter your number on the Deploy page or call /dep/bybot?num= from your bot.' },
                { q: 'Can I add my own server?', a: 'No - You can not add your own servers.' },
                { q: 'Is my data private?', a: 'Session numbers are never exposed in the UI. See our Privacy page.' },
              ].map((f, i) => (
                <FaqItem key={i} q={f.q} a={f.a} delay={`${0.1 + i * 0.1}s`} />
              ))}
            </div>
          </div>

        </div>
      </div>
    </div>
  )
}

// ── FAQ ACCORDION ─────────────────────────────────────────────────────────────
function FaqItem({ q, a, delay }) {
  const [open, setOpen] = useState(false)
  return (
    <div className={`${styles.faqItem} ${open ? styles.faqOpen : ''}`} style={{ animationDelay: delay }}>
      <button className={styles.faqQ} onClick={() => setOpen((o) => !o)}>
        <span>{q}</span>
        <span className={`${styles.faqChevron} ${open ? styles.faqChevronOpen : ''}`}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </span>
      </button>
      <div className={`${styles.faqA} ${open ? styles.faqAOpen : ''}`}>
        <p>{a}</p>
      </div>
    </div>
  )
}
